import React from "react";

export default function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 text-gray-300">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Privacy Policy</h1>
        <p className="text-gray-400 text-sm">Effective Date: May 25, 2026 &nbsp;|&nbsp; Last Updated: May 25, 2026</p>
        <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full mx-auto mt-4" />
      </div>

      <div className="space-y-10 text-sm md:text-base leading-relaxed">

        {/* Section 1 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">1.</span> Introduction
          </h2>
          <p>Welcome to <strong className="text-white">Kronos AI</strong> ("we," "our," or "us"). Kronos AI is an intelligent command center for social media automation, AI-powered proposals, and business management, operated by DevX House and accessible at <a href="https://kronos.devxhouse.com" className="text-cyan-400 hover:underline">https://kronos.devxhouse.com</a>.</p>
          <p className="mt-3">We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.</p>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">2.</span> Information We Collect
          </h2>
          <h3 className="text-white font-semibold mb-2">2.1 Information You Provide Directly</h3>
          <ul className="list-disc list-inside space-y-1 text-gray-300 mb-4">
            <li><strong className="text-white">Account Information</strong>: Name, email address, username, and password when you register</li>
            <li><strong className="text-white">Business Information</strong>: Business name, address, phone number, services, and descriptions you enter for the AI Receptionist feature</li>
            <li><strong className="text-white">Content</strong>: Social media posts, proposals, images, and other content you create or upload</li>
            <li><strong className="text-white">Communications</strong>: Messages or inquiries you send to us</li>
          </ul>
          <h3 className="text-white font-semibold mb-2">2.2 Information Collected Automatically</h3>
          <ul className="list-disc list-inside space-y-1 text-gray-300 mb-4">
            <li><strong className="text-white">Usage Data</strong>: Pages visited, features used, time spent, and navigation patterns</li>
            <li><strong className="text-white">Device Information</strong>: Browser type, operating system, device type</li>
            <li><strong className="text-white">Log Data</strong>: IP address, access times, and error logs</li>
            <li><strong className="text-white">Cookies</strong>: Session tokens and authentication cookies</li>
          </ul>
          <h3 className="text-white font-semibold mb-2">2.3 Information from Third-Party Services</h3>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>Social media profile information from platforms you connect</li>
            <li>Business data processed through Groq AI for the AI Receptionist</li>
            <li>Subscription and billing data processed through Paddle</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">3.</span> How We Use Your Information
          </h2>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>Provide and operate the Kronos AI platform features</li>
            <li>Power your AI Receptionist using your business information</li>
            <li>Generate, schedule, and publish social media content on your behalf</li>
            <li>Create AI-powered proposals using your business data</li>
            <li>Manage your account and authenticate your identity</li>
            <li>Send service updates, security alerts, and support responses</li>
            <li>Process subscription payments and manage your plan</li>
            <li>Analyze usage patterns to improve features and performance</li>
            <li>Detect, prevent, and respond to fraud or security incidents</li>
            <li>Meet our legal obligations</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">4.</span> How We Share Your Information
          </h2>
          <p className="mb-4 text-white font-semibold">We do not sell your personal information.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left px-4 py-3 text-white font-semibold">Provider</th>
                  <th className="text-left px-4 py-3 text-white font-semibold">Purpose</th>
                  <th className="text-left px-4 py-3 text-white font-semibold">Data Shared</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  ["Groq AI", "AI language processing", "Business descriptions, service lists"],
                  ["Pollinations.ai", "Image generation", "Text prompts only"],
                  ["Hugging Face", "Video generation", "Text prompts only"],
                  ["Paddle", "Payment processing", "Billing information"],
                  ["Social Platforms", "Content publishing", "Posts you approve for publishing"],
                ].map(([provider, purpose, data]) => (
                  <tr key={provider} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-cyan-400 font-medium">{provider}</td>
                    <td className="px-4 py-3">{purpose}</td>
                    <td className="px-4 py-3 text-gray-400">{data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">5.</span> Data Storage and Security
          </h2>
          <p className="mb-3">Your data is stored on secure servers hosted at <span className="text-cyan-400">kronos.devxhouse.com</span>. We implement industry-standard security practices including:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>HTTPS/SSL encryption for all data in transit</li>
            <li>Hashed and salted password storage</li>
            <li>Authentication tokens with expiration</li>
            <li>Access controls limiting who can access your data</li>
            <li>Regular security monitoring</li>
          </ul>
          <p className="mt-3 text-gray-400 text-sm">No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">6.</span> Data Retention
          </h2>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li><strong className="text-white">Account Data</strong>: Retained while active and for 30 days after deletion</li>
            <li><strong className="text-white">Content & Posts</strong>: Retained until you delete them or close your account</li>
            <li><strong className="text-white">Usage Logs</strong>: Retained for up to 12 months</li>
            <li><strong className="text-white">Billing Records</strong>: Retained for 7 years as required by financial regulations</li>
          </ul>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">7.</span> Your Rights and Choices
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              ["Access & Portability", "Request a copy of the personal data we hold about you."],
              ["Correction", "Update or correct your account information at any time through account settings."],
              ["Deletion", "Request deletion of your account and data by emailing us. Processed within 30 days."],
              ["Opt-Out", "Opt out of non-essential communications by contacting us."],
              ["Withdraw Consent", "Where we rely on consent, you may withdraw it at any time."],
              ["Data Portability", "Request your data in a portable format for transfer to another service."],
            ].map(([title, desc]) => (
              <div key={title} className="bg-white/5 border border-white/10 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-1">{title}</h4>
                <p className="text-gray-400 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">8.</span> Cookies
          </h2>
          <p className="mb-3">We use minimal cookies strictly necessary for platform operation:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left px-4 py-3 text-white font-semibold">Cookie</th>
                  <th className="text-left px-4 py-3 text-white font-semibold">Purpose</th>
                  <th className="text-left px-4 py-3 text-white font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                <tr><td className="px-4 py-3 text-cyan-400">token</td><td className="px-4 py-3">Authentication session</td><td className="px-4 py-3 text-gray-400">Session</td></tr>
                <tr><td className="px-4 py-3 text-cyan-400">username</td><td className="px-4 py-3">Display your username</td><td className="px-4 py-3 text-gray-400">Session</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-gray-400 text-sm">We do not use advertising or tracking cookies.</p>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">9.</span> AI Receptionist and Phone Data
          </h2>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>Business information you provide is processed by Groq AI to power your assistant</li>
            <li>Call handling data may be logged for quality and debugging purposes</li>
            <li>We do not record or store actual phone call audio</li>
            <li>You are responsible for informing callers they are interacting with an AI assistant, as required by applicable laws in your jurisdiction</li>
          </ul>
        </section>

        {/* Section 10 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">10.</span> Children's Privacy
          </h2>
          <p>Kronos AI is not directed at children under the age of 16. We do not knowingly collect personal information from children under 16. If you believe we have inadvertently collected such information, please contact us immediately at <a href="mailto:kronos@devxhouse.com" className="text-cyan-400 hover:underline">kronos@devxhouse.com</a> and we will delete it promptly.</p>
        </section>

        {/* Section 11 */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">11.</span> Changes to This Policy
          </h2>
          <p>We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date at the top of this page and notify you via email if changes are material. Your continued use of the platform after changes constitutes acceptance of the updated policy.</p>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span className="text-cyan-400">12.</span> Contact Us
          </h2>
          <p className="mb-4">If you have questions, concerns, or requests regarding this Privacy Policy or your personal data:</p>
          <div className="space-y-2">
            <p><span className="text-white font-semibold">Kronos AI by DevX House</span></p>
            <p>📧 <a href="mailto:kronos@devxhouse.com" className="text-cyan-400 hover:underline">kronos@devxhouse.com</a></p>
            <p>🌐 <a href="https://kronos.devxhouse.com" className="text-cyan-400 hover:underline">https://kronos.devxhouse.com</a></p>
            <p className="text-gray-400 text-sm mt-2">We will respond to all requests within 30 days.</p>
          </div>
        </section>

        <p className="text-center text-gray-500 text-sm pt-4 border-t border-white/10">
          This Privacy Policy was last updated on May 25, 2026.
        </p>
      </div>
    </div>
  );
}
